import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PlanActionDrawer } from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/PlanActionDrawer';
import { PlanFormView } from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/PlanFormView';
import {
  usePlanLibraryPlans,
  type PlanLibraryForm,
  type PlanLibraryPlan,
  type PlanLibraryTask,
} from 'hooks/planLibrary/planLibraryHooks';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useRosterTemplateLibrary } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useFilledRosterTemplates,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';

import {
  filledRosterTemplateForPlan,
  planForResponsibleParty,
  plansForResponsibleParty,
  shouldAutoSelectResponsiblePartyPlan,
  type CreateIncidentFormValues,
} from './createIncidentModel';
import { useCreateRosterDraft } from './CreateRosterDraftContext';
import { DraftRosterWorkspace } from './DraftRosterWorkspace';
import { defaultDraftSeats } from './draftRosterModel';
import { useHwcgFlag } from 'utils/featureFlags';
import { HwcgRosterActivationFields } from './HwcgRosterActivationFields';
import { rosterActivateScript } from './hwcgEocNotice';

/**
 * Plan section under Group Actions on Incident Specifics. A plan is required.
 * Selecting one with a filled roster seeds the draft chart below; seats can be
 * edited before create.
 */
export function StepApplyPlan() {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const planId = watch('plan_id');
  const companyName = watch('hwcg_specifics.companyName');
  const hwcg = useHwcgFlag();
  const { data: plans = [], isLoading } = usePlanLibraryPlans({
    ensureHwcg: hwcg,
  });
  const manualChoiceRef = useRef<{
    planId: string | null;
    company: string;
  } | null>(null);
  const [query, setQuery] = useState('');
  const [collapsedPlanId, setCollapsedPlanId] = useState<string | null>(null);
  const filledTemplates = useFilledRosterTemplates();
  const library = useRosterTemplateLibrary();
  const catalog = useRosterTemplatesCatalog();
  const { applyFilledRoster } = useCreateRosterDraft();
  const appliedPlanRef = useRef<string | null>(null);

  const visiblePlans = useMemo(
    () => (hwcg ? plansForResponsibleParty(plans, companyName) : plans),
    [companyName, hwcg, plans]
  );

  const filteredPlans = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return visiblePlans;
    return visiblePlans.filter((plan) =>
      plan.name.toLowerCase().includes(needle)
    );
  }, [query, visiblePlans]);

  const selected = useMemo(
    () => visiblePlans.find((plan) => plan.id === planId) ?? null,
    [visiblePlans, planId]
  );

  useEffect(() => {
    if (!selected?.filledRosterTemplateId) return;
    if (appliedPlanRef.current === selected.id) return;
    const template = filledRosterTemplateForPlan(
      filledTemplates.data ?? [],
      selected.filledRosterTemplateId
    );
    if (!template) return;
    const nodes = nodesFor(
      template,
      library.data ?? [],
      catalog.data?.templates ?? []
    );
    if (!nodes.length) return;
    applyFilledRosterToDraft({
      template,
      nodes,
      applyFilledRoster,
      setValue,
    });
    appliedPlanRef.current = selected.id;
  }, [
    applyFilledRoster,
    catalog.data?.templates,
    filledTemplates.data,
    library.data,
    selected,
    setValue,
  ]);

  useEffect(() => {
    if (!hwcg) return;
    const company = companyName?.trim() ?? '';
    const match = planForResponsibleParty(plans, company);
    const manual = manualChoiceRef.current;
    if (
      !shouldAutoSelectResponsiblePartyPlan({
        companyName: company,
        currentPlanId: planId,
        matchId: match?.id ?? null,
        manualPlanId: manual?.planId ?? null,
        manualCompany: manual?.company ?? null,
      })
    ) {
      return;
    }
    if (!match) return;
    setCollapsedPlanId(null);
    setValue('plan_id', match.id, { shouldDirty: true });
    setValue('plan_name', match.name ?? null, { shouldDirty: true });
    if (!match.filledRosterTemplateId) {
      setValue('hwcg_filled_roster_template_id', null, { shouldDirty: true });
      setValue('hwcg_roster_resources', [], { shouldDirty: true });
      appliedPlanRef.current = match.id;
    }
  }, [companyName, hwcg, planId, plans, setValue]);

  const choose = (plan: PlanLibraryPlan | null) => {
    manualChoiceRef.current = {
      planId: plan?.id ?? null,
      company: companyName?.trim() ?? '',
    };
    setValue('plan_id', plan?.id ?? null, { shouldDirty: true });
    setValue('plan_name', plan?.name ?? null, { shouldDirty: true });
    if (!plan?.filledRosterTemplateId) {
      setValue('hwcg_filled_roster_template_id', null, { shouldDirty: true });
      setValue('hwcg_roster_resources', [], { shouldDirty: true });
      setValue('hwcg_roster_activation', false, { shouldDirty: true });
      appliedPlanRef.current = plan?.id ?? null;
    }
  };

  return (
    <div
      className="tw:flex tw:h-full tw:w-full tw:flex-col tw:gap-4 tw:overflow-y-auto tw:py-2"
      data-tour={hwcg ? 'hwcg-create-apply-plan' : 'create-incident-apply-plan'}
    >
      <div className="tw:shrink-0">
        <h2 className="tw:m-0 tw:text-lg tw:font-semibold">
          Apply Plan{' '}
          {hwcg ? <span className="tw:text-destructive">*</span> : null}
        </h2>
        <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-muted-foreground">
          {hwcg
            ? 'Choose a plan for this responsible party. Its filled roster loads below so you can adjust seats before creating the incident. Tasks and ICS forms are created after the workspace exists.'
            : 'Optionally choose a plan. Its roster loads below so you can adjust seats; tasks and ICS forms are added after the incident is created.'}
        </p>
      </div>

      {isLoading ? (
        <p className="tw:text-sm tw:text-muted-foreground">Loading plans…</p>
      ) : visiblePlans.length === 0 ? (
        <p className="tw:text-sm tw:text-muted-foreground">
          {hwcg
            ? 'No plans match this responsible party yet. Tag a plan in Hub → Plan Library, or create an untagged plan.'
            : 'No plans yet. Create one in Hub → Plan Library, or create the incident without a plan.'}
        </p>
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search plans by name"
            aria-label="Search plans by name"
            className="tw:shrink-0"
          />
          {!hwcg && planId ? (
            <button
              type="button"
              className="tw:self-start tw:bg-transparent tw:p-0 tw:text-sm tw:text-muted-foreground tw:underline tw:underline-offset-2"
              onClick={() => {
                setCollapsedPlanId(null);
                choose(null);
                appliedPlanRef.current = null;
                setValue('roster_seats', defaultDraftSeats(), {
                  shouldDirty: true,
                });
                setValue('roster_assignments', [], { shouldDirty: true });
                setValue('people', [], { shouldDirty: true });
                setValue('roster_template_slug', null, { shouldDirty: true });
                setValue('roster_template_name', null, { shouldDirty: true });
                setValue('hwcg_roster_externals', [], { shouldDirty: true });
              }}
            >
              Clear plan
            </button>
          ) : null}
          {filteredPlans.length === 0 ? (
            <p className="tw:text-sm tw:text-muted-foreground">
              No plans match that name.
            </p>
          ) : (
            <RadioGroup
              aria-label="Apply plan"
              className="tw:gap-2"
              value={planId ?? ''}
              onValueChange={() => {
                // The group can report an untagged plan while the list mounts.
                // A row click is the only manual choice.
              }}
            >
              {filteredPlans.map((plan) => {
                const expanded =
                  plan.id === planId && collapsedPlanId !== plan.id;
                const inputId = `apply-plan-${plan.id}`;
                return (
                  <div
                    key={plan.id}
                    className="tw:overflow-hidden tw:rounded-md tw:border tw:border-border"
                  >
                    <div
                      className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:px-3 tw:py-2"
                      onClick={() => {
                        if (plan.id === planId) {
                          setCollapsedPlanId((current) =>
                            current === plan.id ? null : plan.id
                          );
                          return;
                        }
                        setCollapsedPlanId(null);
                        choose(plan);
                      }}
                    >
                      <RadioGroupItem value={plan.id} id={inputId} />
                      <Label
                        htmlFor={inputId}
                        className="tw:min-w-0 tw:flex-1 tw:text-sm tw:font-medium tw:break-words"
                      >
                        {plan.name}
                      </Label>
                    </div>
                    {expanded && selected ? (
                      <PlanContents
                        plan={selected}
                        rosterNames={rosterTemplateNames(
                          selected,
                          library.data ?? [],
                          catalog.data?.templates ?? [],
                          filledTemplates.data ?? []
                        )}
                        hideTemplateSidebar={planHasRosterTemplate(selected)}
                      />
                    ) : null}
                  </div>
                );
              })}
            </RadioGroup>
          )}
        </div>
      )}
    </div>
  );
}

function RosterActivationFields() {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const enabled = watch('hwcg_roster_activation');
  const notice = watch('hwcg_notices.responsible_party');
  const specifics = watch('hwcg_specifics');
  const incidentName = watch('incident_name');
  const hwcg = useHwcgFlag();

  return (
    <HwcgRosterActivationFields
      template={
        hwcg
          ? undefined
          : rosterActivateScript(
              incidentName,
              notice.locationLabel,
              notice.reportToTime
            )
      }
      enabled={enabled}
      onEnabledChange={(value) =>
        setValue('hwcg_roster_activation', value, { shouldDirty: true })
      }
      notice={notice}
      onNoticeChange={(next) =>
        setValue('hwcg_notices.responsible_party', next, { shouldDirty: true })
      }
      specifics={specifics}
      checkboxLabel="Send activation requests to the people on this filled roster"
      idPrefix="roster"
    />
  );
}

function planHasRosterTemplate(plan: PlanLibraryPlan): boolean {
  return (
    Boolean(plan.filledRosterTemplateId) || plan.rosterTemplateIds.length > 0
  );
}

function rosterTemplateNames(
  plan: PlanLibraryPlan,
  library: { id: string; name: string }[],
  catalog: { slug: string; name: string }[],
  filled: { id: string; name: string }[]
): string[] {
  const names = plan.rosterTemplateIds.map(
    (id) =>
      library.find((entry) => entry.id === id)?.name ??
      catalog.find((entry) => entry.slug === id)?.name ??
      id
  );
  if (plan.filledRosterTemplateId) {
    names.push(
      filled.find((entry) => entry.id === plan.filledRosterTemplateId)?.name ??
        'Filled roster'
    );
  }
  return names;
}

function PlanContents({
  plan,
  rosterNames,
  hideTemplateSidebar,
}: {
  plan: PlanLibraryPlan;
  rosterNames: string[];
  hideTemplateSidebar: boolean;
}) {
  const forms = plan.forms ?? [];
  const files = plan.files ?? [];
  const layers = plan.layers ?? [];
  const [taskPreview, setTaskPreview] = useState<PlanLibraryTask | null>(null);
  const [formPreview, setFormPreview] = useState<PlanLibraryForm | null>(null);
  const hwcg = useHwcgFlag();
  const [rosterFullScreen, setRosterFullScreen] = useState(false);
  const empty =
    !plan.description?.trim() &&
    plan.tasks.length === 0 &&
    forms.length === 0 &&
    files.length === 0 &&
    layers.length === 0 &&
    rosterNames.length === 0;

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-border tw:px-3 tw:py-3">
      {empty ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          This plan has no description, tasks, ICS forms, files, map layers, or
          roster templates yet.
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-3">
          {plan.description?.trim() ? (
            <p className="tw:m-0 tw:text-sm tw:whitespace-pre-wrap">
              {plan.description}
            </p>
          ) : null}
          {plan.tasks.length > 0 ? (
            <ContentList title="Tasks">
              {plan.tasks.map((task) => (
                <li
                  key={task.id}
                  className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:py-1"
                >
                  <button
                    type="button"
                    className="tw:min-w-0 tw:flex-1 tw:bg-transparent tw:p-0 tw:text-left tw:break-words tw:underline tw:underline-offset-2"
                    onClick={() => setTaskPreview(task)}
                  >
                    {task.name}
                  </button>
                  <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
                    {task.assignee_kind === 'position' && task.assignee_id ? (
                      <Badge variant="outline">{task.assignee_id}</Badge>
                    ) : null}
                    {task.status ? (
                      <Badge variant="outline">{task.status}</Badge>
                    ) : null}
                    <Badge variant="outline">{task.priority}</Badge>
                  </span>
                </li>
              ))}
            </ContentList>
          ) : null}
          {forms.length > 0 ? (
            <ContentList title="ICS forms">
              {forms.map((form) => (
                <li key={form.id} className="tw:py-1">
                  <button
                    type="button"
                    className="tw:bg-transparent tw:p-0 tw:text-left tw:underline tw:underline-offset-2"
                    onClick={() => setFormPreview(form)}
                  >
                    {form.label}
                  </button>
                </li>
              ))}
            </ContentList>
          ) : null}
          {files.length > 0 ? (
            <ContentList title="Files">
              {files.map((file) => (
                <li key={file.fileId} className="tw:py-1">
                  {file.name}
                </li>
              ))}
            </ContentList>
          ) : null}
          {layers.length > 0 ? (
            <ContentList title="Map layers">
              {layers.map((layer) => (
                <li key={`${layer.kind}:${layer.id}`} className="tw:py-1">
                  {layer.name}
                </li>
              ))}
            </ContentList>
          ) : null}
          {rosterNames.length > 0 ? (
            <ContentList title="Roster templates">
              {rosterNames.map((name, index) => (
                <li key={`${name}:${index}`} className="tw:py-1">
                  {name}
                </li>
              ))}
            </ContentList>
          ) : null}
        </div>
      )}
      {plan.filledRosterTemplateId ? <RosterActivationFields /> : null}
      <div
        className={
          hwcg && rosterFullScreen
            ? 'dark tw:fixed tw:inset-0 tw:z-50 tw:overflow-auto tw:bg-background tw:p-4'
            : hwcg
              ? 'tw:min-h-[600px] tw:overflow-auto tw:rounded-md tw:border tw:border-border tw:px-3 tw:pt-5 tw:pb-3'
              : 'tw:min-h-[600px] tw:overflow-auto tw:rounded-md tw:border tw:border-border'
        }
      >
        <DraftRosterWorkspace
          hideTemplateSidebar={hideTemplateSidebar}
          fullScreen={hwcg && rosterFullScreen}
          onFullScreenChange={hwcg ? setRosterFullScreen : undefined}
        />
      </div>
      {taskPreview ? (
        <PlanActionDrawer
          task={taskPreview}
          siblingTasks={plan.tasks}
          readOnly
          workspaceLabel="Plan template"
          onClose={() => setTaskPreview(null)}
        />
      ) : null}
      {formPreview && plan.templateIncidentId ? (
        <PlanFormView
          planName={plan.name}
          form={formPreview}
          templateIncidentId={plan.templateIncidentId}
          onClose={() => setFormPreview(null)}
        />
      ) : null}
      {formPreview && !plan.templateIncidentId ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setFormPreview(null);
          }}
        >
          <DialogContent className="tw:sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{formPreview.label}</DialogTitle>
              <DialogDescription>
                This form has no template incident yet, so there is nothing to
                preview.
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function ContentList({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="tw:m-0 tw:text-xs tw:font-medium tw:text-muted-foreground">
        {title}
      </p>
      <ul className="tw:m-0 tw:list-none tw:p-0 tw:text-sm">{children}</ul>
    </div>
  );
}

function applyFilledRosterToDraft({
  template,
  nodes,
  applyFilledRoster,
  setValue,
}: {
  template: FilledRosterTemplate;
  nodes: RosterTemplateNodeDto[];
  applyFilledRoster: ReturnType<
    typeof useCreateRosterDraft
  >['applyFilledRoster'];
  setValue: ReturnType<
    typeof useFormContext<CreateIncidentFormValues>
  >['setValue'];
}) {
  const people = template.assignments.flatMap((row) =>
    row.userGuid
      ? [
          {
            seatName: row.seatName,
            user_guid: row.userGuid,
            name: row.name,
            email_address: row.email,
          },
        ]
      : []
  );
  applyFilledRoster({
    nodes,
    slug: template.sourceSlug || template.sourceTemplateId || template.id,
    name: template.name,
    people,
  });
  setValue(
    'hwcg_roster_externals',
    template.assignments
      .filter((row) => !row.userGuid)
      .map((row) => ({
        seatName: row.seatName,
        name: row.name,
        email: row.email,
        phone: row.phone,
      })),
    { shouldDirty: true }
  );
  setValue('hwcg_filled_roster_template_id', template.id, {
    shouldDirty: true,
  });
  setValue('hwcg_roster_resources', template.resources ?? [], {
    shouldDirty: true,
  });
}

function nodesFor(
  template: FilledRosterTemplate,
  library: { id: string; nodes: RosterTemplateNodeDto[] }[],
  catalog: { slug: string; nodes?: RosterTemplateNodeDto[] }[]
): RosterTemplateNodeDto[] {
  if (template.sourceTemplateId) {
    return (
      library.find((entry) => entry.id === template.sourceTemplateId)?.nodes ??
      []
    );
  }
  if (template.sourceSlug) {
    return (
      catalog.find((entry) => entry.slug === template.sourceSlug)?.nodes ?? []
    );
  }
  return [];
}
